/** How long a placed-request marker keeps counting as "this device asked". */
export const OWN_QUEUE_REQUEST_WINDOW_MS = 10 * 60 * 1000;
/**
 * Whether a delivered queue match may silently auto-enter the room on this
 * device.
 *
 * Client accounts can be signed in on several devices at once, and matches are
 * broadcast to all of them so pushes can wake sleeping phones. Only the device
 * that placed the request (the own-request marker) — or one already on this
 * very call — may auto-enter; the rest ring instead. Without that rule two
 * signed-in devices take turns superseding each other's room session and the
 * camera strobes on/off (f22ef958).
 *
 * The interpreter side is different in kind: the server confirms an accept
 * only to the socket that sent it, so an interpreter-role match IS this
 * device's accept. Interpreters never write the own-request marker (it is set
 * in the client request path), so gating them on it silently ate the entire
 * accept→room flow on the terp app — Accept removed the card, the reducer
 * showed "Active Session", and no navigation ever happened.
 */
export function shouldAutoEnterMatchedRoom(options) {
    if (options.role === 'interpreter') {
        return true;
    }
    if (options.storedCallId && options.matchCallId && options.storedCallId === options.matchCallId) {
        return true;
    }
    const now = options.now ?? Date.now();
    return Boolean(options.ownRequestAt
        && now - Number(options.ownRequestAt) < OWN_QUEUE_REQUEST_WINDOW_MS);
}
export function conferenceErrorDetails(error) {
    if (!error || typeof error !== 'object') {
        return {};
    }
    const candidate = error;
    return {
        message: typeof candidate.message === 'string' ? candidate.message.slice(0, 500) : undefined,
        name: typeof candidate.name === 'string' ? candidate.name.slice(0, 120) : undefined
    };
}
export function getConferenceRoomName(conference) {
    if (!conference || typeof conference !== 'object') {
        return undefined;
    }
    try {
        const name = conference.getName?.();
        return typeof name === 'string' && name.trim() ? name.trim() : undefined;
    }
    catch {
        return undefined;
    }
}
export function isConferenceForActiveRoom(activeRoomName, conference) {
    return Boolean(activeRoomName && getConferenceRoomName(conference) === activeRoomName);
}
export function shouldPreserveMatchedCallAfterExit(conferenceJoined, activeRoomName, joinedConference, exitingConference, exitWasRequested = true) {
    if (!exitWasRequested || !conferenceJoined || !joinedConference) {
        return true;
    }
    // A room can briefly have two conference objects while Jitsi replaces a
    // failed/stale join. A stale object leaving must never end the live call.
    if (joinedConference !== exitingConference) {
        return true;
    }
    const exitingRoomName = getConferenceRoomName(exitingConference);
    // Only an explicit leave of the exact joined conference may end the call.
    return !exitingRoomName || !activeRoomName || exitingRoomName !== activeRoomName;
}
